import React from 'react';
import { formatPreparationEvidenceCitation } from './preparationSourceAuthors';
import {
  Box, Button, ButtonBase, Collapse, Divider, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircleRounded as FoundIcon,
  ChevronRightRounded as ChevronIcon,
  ExpandMoreRounded as ExpandIcon,
  HelpRounded as UncertainIcon,
  RadioButtonUncheckedRounded as MissingIcon,
} from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import PreparationEvidenceReview from './PreparationEvidenceReview';
import {
  PREPARATION_EVIDENCE_GROUPS,
  getVisiblePreparationEvidence,
  groupPreparationEvidence,
  groupMedicationPreparationEvidence,
} from './preparationJourneyPresentation';

const STATUS_PRESENTATION = {
  found: { Icon: FoundIcon, label: 'Found', color: '#3F806F' },
  uncertain: { Icon: UncertainIcon, label: 'Needs a quick check', color: '#A76E15' },
  not_found: { Icon: MissingIcon, label: 'Not found in Lifelog', color: '#697386' },
};

const formatContextLabel = (value = '') => String(value || '')
  .split('_')
  .join(' ')
  .split('-')
  .join(' ')
  .trim();

const getEvidenceMeta = (item) => [
  formatPreparationEvidenceCitation(item),
  item.provenanceLabel || (
    ['timeline', 'dailyLog'].includes(item.sourceType)
      ? 'Timeline'
      : item.sourceType === 'document' ? 'Document' : 'Profile'
  ),
  formatContextLabel(item.contextLabel),
].filter(Boolean).join(' · ');

const PreparationJourneyArea = ({
  area,
  index,
  expanded,
  sourcesExpanded,
  showAllEvents,
  canAdd,
  accent,
  onToggle,
  onToggleSources,
  onShowAllEvents,
  onOpenEvidence,
  onOpenReferences,
  decidingSourceId,
  onEvidenceDecision,
  onAdd,
}) => {
  const presentation = STATUS_PRESENTATION[area.status] || STATUS_PRESENTATION.not_found;
  const groups = area.id === 'medication_health_changes'
    ? groupMedicationPreparationEvidence(area.evidence || [])
    : groupPreparationEvidence(area.evidence || []);
  const hasIncludedEvidence = groups.length > 0;
  const hasEvidence = hasIncludedEvidence || Boolean(area.possibleEvidence?.length);

  return (
    <Box sx={{ borderTop: index ? `1px solid ${alpha(colors.semantic.ink, 0.1)}` : 0 }}>
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.45, display: 'flex', gap: 1.1, alignItems: 'flex-start' }}>
        <presentation.Icon sx={{ mt: 0.15, fontSize: 22, color: presentation.color, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 750, color: colors.semantic.ink, lineHeight: 1.2 }}>{area.label}</Typography>
          <Typography sx={{ mt: 0.3, fontSize: '0.82rem', color: presentation.color, fontWeight: 600, lineHeight: 1.3 }}>
            {area.status === 'not_found' ? presentation.label : area.summary}
          </Typography>
        </Box>
        {hasEvidence ? (
          <ButtonBase
            onClick={onToggle}
            aria-label={`${expanded ? 'Hide' : 'View'} ${area.label}`}
            aria-expanded={expanded}
            sx={{ flexShrink: 0, gap: 0.2, color: accent.ink, borderRadius: '999px', px: 0.65, py: 0.45 }}
          >
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 750 }}>{expanded ? 'Hide' : 'View'}</Typography>
            <ExpandIcon sx={{ fontSize: 19, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
          </ButtonBase>
        ) : canAdd && area.addAction ? (
          <ButtonBase onClick={onAdd} aria-label={`Add ${area.label}`} sx={{ flexShrink: 0, color: accent.ink, borderRadius: '999px', px: 0.8, py: 0.45 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 900 }}>Add</Typography>
          </ButtonBase>
        ) : null}
      </Box>

      <Collapse in={expanded && hasEvidence} unmountOnExit>
        <Stack spacing={1.25} sx={{ px: { xs: 1.5, md: 2 }, pb: 1.7, pl: { xs: 5.05, md: 5.55 } }}>
          {(area.summaryBullets || []).length ? (
            <Box>
              <Typography sx={{ mb: 0.5, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.58) }}>
                Lifelog summary
              </Typography>
              <Box component="p" sx={{ m: 0, color: colors.semantic.ink }}>
                {area.summaryBullets.map((bullet, bulletIndex) => (
                  <React.Fragment key={`${area.id}-summary-${bulletIndex}`}>
                    <Typography component="span" sx={{ fontSize: '0.9rem', fontWeight: 500, lineHeight: 1.58 }}>
                      {bullet.text}{' '}
                    </Typography>
                    {bullet.sourceIds?.length ? (
                      <ButtonBase
                        component="span"
                        onClick={() => onOpenReferences(bullet.sourceIds)}
                        aria-label={`View ${bullet.sourceIds.length} source${bullet.sourceIds.length === 1 ? '' : 's'} for this summary sentence`}
                        sx={{ mr: 0.45, mb: 0.2, px: 0.45, py: 0.05, verticalAlign: 'baseline', borderRadius: '6px', color: accent.ink, bgcolor: alpha(accent.primary, 0.1), fontSize: '0.72rem', fontWeight: 750 }}
                      >
                        [{bullet.sourceIds.length === 1 ? '1' : `1–${bullet.sourceIds.length}`}]
                      </ButtonBase>
                    ) : null}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          ) : null}
          {area.id === 'medication_health_changes' ? (
            <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.4, color: alpha(colors.semantic.ink, 0.62) }}>
              Lifelog checks observations recorded during the 30 days after each confirmed medication change.
            </Typography>
          ) : null}
          {area.id === 'current_iep_evaluation' && area.currentDocumentMissing && canAdd && area.addAction ? (
            <Button
              onClick={onAdd}
              sx={{
                alignSelf: 'flex-start', minWidth: 0, p: 0, textTransform: 'none',
                fontSize: '0.84rem', fontWeight: 850, color: accent.ink,
              }}
            >
              Add current IEP or evaluation
            </Button>
          ) : null}
          <PreparationEvidenceReview
            items={area.possibleEvidence || []}
            canEdit={canAdd}
            accent={accent}
            decidingSourceId={decidingSourceId}
            onOpen={onOpenEvidence}
            onDecide={onEvidenceDecision}
          />
          {hasIncludedEvidence ? (
            <>
              <ButtonBase onClick={onToggleSources} aria-expanded={sourcesExpanded} sx={{ alignSelf: 'flex-start', color: accent.ink, borderRadius: '999px', px: 0.4, py: 0.3 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{sourcesExpanded ? 'Hide sources' : `View sources (${area.evidence.length})`}</Typography>
                <ExpandIcon sx={{ fontSize: 18, transform: sourcesExpanded ? 'rotate(180deg)' : 'none' }} />
              </ButtonBase>
              <Collapse in={sourcesExpanded} unmountOnExit>
                <Stack spacing={1.25}>
          {groups.map(({ group, label, items }, groupIndex) => {
            const visibleItems = getVisiblePreparationEvidence({ group, items, showAll: showAllEvents });
            const hiddenCount = items.length - visibleItems.length;
            return (
              <Stack key={group} spacing={0.65}>
                {groupIndex ? <Divider /> : null}
                <Typography sx={{ pt: groupIndex ? 0.7 : 0, fontSize: '0.72rem', fontWeight: 750, letterSpacing: '0.06em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.58) }}>
                  {label}
                </Typography>
                {visibleItems.map((item) => (
                  <ButtonBase
                    key={`${item.sourceType}:${item.sourceId}`}
                    onClick={() => onOpenEvidence(item)}
                    sx={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left', borderRadius: '12px', py: 0.65, px: 0.75, mx: -0.75, '&:hover': { bgcolor: alpha(accent.primary, 0.06) } }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ mb: 0.28, fontSize: '0.67rem', lineHeight: 1.25, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: alpha(colors.semantic.ink, 0.58) }}>
                        {getEvidenceMeta(item)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.88rem', lineHeight: 1.42, fontWeight: 550, color: colors.semantic.ink, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {item.description || item.label}
                      </Typography>
                    </Box>
                    <ChevronIcon sx={{ ml: 0.6, fontSize: 19, color: accent.ink, flexShrink: 0 }} />
                  </ButtonBase>
                ))}
                {group === PREPARATION_EVIDENCE_GROUPS.RECENT_EVENTS && hiddenCount > 0 ? (
                  <Button onClick={onShowAllEvents} sx={{ alignSelf: 'flex-start', minWidth: 0, p: 0, textTransform: 'none', fontWeight: 850, color: accent.ink }}>
                    Show all {items.length} events
                  </Button>
                ) : null}
              </Stack>
            );
          })}
                </Stack>
              </Collapse>
            </>
          ) : null}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default PreparationJourneyArea;
